import { useContext, useEffect, useState } from "react";
import ListStickyHeadings from "../../../components/sessions/SessionsStickyHeadings";
import SessionModel from "../../../api/models/SessionModel";
import StudentSessionInfo from "../../../components/sessions/StudentSessionsInfo";
import { ArrowLeftIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";
import FullScreenModal from "../../../components/modal/FullScreenModal";
import SessionPlaceholder from "../../../assets/img/session-placeholeder.png";
import { indexStudentSessions } from "../../../api/endpoints/students/sessions";
import { indexStudentSchedules } from "../../../api/endpoints/students/schedules";
import { classNames, showApiErrorMessages } from "../../../helpers";
import ScheduleModel from "../../../api/models/ScheduleModel";
import CalendarScheduleSlotModel from "../../../models/CalendarScheduleSlotModel";
import {
  getSessionsFromScheduleSlots,
  groupSessionsByMonth,
  renumberSessions,
} from "../../../components/sessions/helper";
import PlaceHolder from "../../../components/placeHolder.tsx/PlaceHolder";
import SessionsPagination from "../../../components/sessions/SessionsPagination";
import { sessionPagination } from "../../../helpers/pagination";
import { useEffectOnce } from "../../../hooks/useEffectOnce";
import { ApiResponse } from "../../../api/apiResponse";
import ScheduleSlotModel from "../../../api/models/ScheduleSlotModel";
import { useInfiniteStudentSessions } from "../../../hooks/useInfiniteStudentSessions";
import { ScrollContainerRef } from "../../../components/Layouts/SidebarWithHaeder";
import { useQuery } from "react-query";
import LoadingSpinner from "../../../components/LoadingSpinner";

function SessionsPage() {
  const { ref } = useContext(ScrollContainerRef);
  const LocalBasePath = "page.student.dashboard";
  const { t } = useTranslation();
  const [sessionsModal, setSessionsModal] = useState<boolean>(false);
  const [currentSession, setCurrentSession] =
    useState<SessionModel | null>(null);
  const [filters, setFilters] = useState({});
  const [currentInViewCardId, setCurrentInViewCard] = useState<number>(0);
  const [disabledNext, setDisabledNext] = useState<boolean>(true);
  const [disabledPrev, setDisabledPrev] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState(true);

  const { sessions, loadMoreSessions, isFetchingSessions } =
    useInfiniteStudentSessions(indexStudentSessions, {
      page_size: 50,
      ...filters,
    });
  const firstSession = sessions[0];
  const lastDoneSession = sessions.at(-1);
  const lastSessionNumber = lastDoneSession?.sessionNumber || 0;

  const { data: scheduledSessions } = useQuery(
    ["studentScheduledSessions"],
    async () => {
      setIsLoading(true);
      const res = await indexStudentSchedules();
      setIsLoading(false);
      return res;
    },
    {
      enabled: !isFetchingSessions,
      select: (apiResponse: ApiResponse) => {
        if (apiResponse.isSuccess) {
          const scheduleSlots: CalendarScheduleSlotModel[] =
            ScheduleModel.fromApiResponse(apiResponse.data).slots?.map(
              (slot: ScheduleSlotModel) => new CalendarScheduleSlotModel(slot)
            );
          const _scheduledSessions = getSessionsFromScheduleSlots(
            scheduleSlots || []
          );
          const scheduledWithSessionNumber = renumberSessions(
            _scheduledSessions,
            lastSessionNumber
          );

          return scheduledWithSessionNumber;
        } else {
          showApiErrorMessages(apiResponse);
        }
      },
    }
  );

  const groupedSessions = groupSessionsByMonth([
    ...sessions,
    ...(scheduledSessions || []),
  ]);

  const ScrollToTopOfNotes = () => {
    if (!!ref) {
      setTimeout(() => {
        ref?.current?.scroll({
          top: 0,
          behavior: "smooth",
        });
      }, 100);
    }
  };
  useEffect(() => {
    ScrollToTopOfNotes();
  }, [currentSession]);
  useEffectOnce(() => {
    onSessionClick(lastDoneSession?.id!);
    setCurrentInViewCard(lastDoneSession?.id!);
  }, !!groupedSessions && !!lastDoneSession);

  const onNavigatePrevOrNext = (dir: "next" | "prev") => {
    if (!sessions.length) {
      return;
    }
    const currentSessionIndex = sessions.findIndex(
      (session) => session.id === currentSession?.id
    );
    const targetSessionIndex =
      dir === "next" ? currentSessionIndex + 1 : currentSessionIndex - 1;
    handlePagination(targetSessionIndex);
  };

  const getByIdSessions = (
    sessionId: number | string,
    sessions: SessionModel[]
  ) => {
    return sessions.filter((session) => session.id === sessionId)[0];
  };

  const navigateToSession = (sessionId: number) => {
    setCurrentSession(getByIdSessions(sessionId, sessions));
    sessionPagination(sessionId, sessions);
    setSessionsModal(false);
  };
  const handlePagination = (targetSessionIndex: number) => {
    const paginationData = sessionPagination(targetSessionIndex, sessions);
    if (!!paginationData) {
      setDisabledNext(paginationData["disabledNext"]);
      setDisabledPrev(paginationData["disabledPrev"]);
      navigateToSession(paginationData["sessionData"].id);
    }
  };

  const onSessionClick = (sessionId: number) => {
    const targetSessionIndex = sessions.findIndex(
      (session) => session.id === sessionId
    );
    handlePagination(targetSessionIndex);
  };

  const onReachTop = () => {
    loadMoreSessions()?.then(() => {
      setCurrentInViewCard(firstSession?.id!);
    });
  };
  const onSearch = (val: object | null) => {
    if (!!val) {
      setFilters(val);
    } else {
      setFilters({});
    }
  };
  useEffect(() => {
    if (sessions?.length === 0) {
      const timer = setTimeout(() => {
        setFilters({});
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [sessions]);
  return (
    <div className="relative flex w-full">
      {Object.keys(groupedSessions).length > 0 ? (
        <>
          {/* Start Side column (hidden on smaller screens) */}
          <aside
            className={classNames(
              "hidden sticky top-0 xl:flex xl:flex-col flex-shrink-0 h-screen w-96 border border-l border-gray-200 overflow-y-auto",
              ref,
              ref > 0 && "h-screen"
            )}
          >
            {!!sessions && (
              <ListStickyHeadings
                onClick={onSessionClick}
                sessions={groupedSessions}
                userType="student"
                onReachTop={onReachTop}
                currentInViewCardId={currentInViewCardId}
                onSearch={onSearch}
                filters={filters}
                enableStudents={false}
              />
            )}
          </aside>
          {/* End Side column */}
          {/* Start main area*/}
          <main className="flex-1 mb-[65px] xl:mb-0 relative z-0 overflow-y-auto border focus:outline-none">
            <button
              data-cy="openSessionsModal"
              onClick={() => setSessionsModal(!sessionsModal)}
              className="flex justify-between w-full px-6 py-1 text-sm font-medium text-gray-500 border-t border-b border-gray-200 xl:hidden bg-gray-50"
            >
              <h3 className="inline-flex py-4">
                <ArrowLeftIcon className="w-5 h-5 mx-1 rtl:rotate-180" />
                {t(`${LocalBasePath}.sessionsList.readingSessions`)}{" "}
              </h3>
            </button>
            <FullScreenModal setOpen={setSessionsModal} open={sessionsModal}>
              {!!sessions && (
                <ListStickyHeadings
                  onClick={onSessionClick}
                  sessions={groupedSessions}
                  userType="student"
                  onReachTop={onReachTop}
                  currentInViewCardId={currentInViewCardId}
                  onSearch={onSearch}
                  filters={filters}
                  enableStudents={false}
                />
              )}
            </FullScreenModal>
            {!!currentSession && (
              <StudentSessionInfo session={currentSession} />
            )}
            <SessionsPagination
              onClickNext={() => onNavigatePrevOrNext("next")}
              onClickPrev={() => onNavigatePrevOrNext("prev")}
              disabledNext={disabledNext}
              disabledPrev={disabledPrev}
              nextButtonLabel={t(`${LocalBasePath}.sessionInfo.nextSession`)}
              prevButtonLabel={t(`${LocalBasePath}.sessionInfo.previseSession`)}
            />
          </main>
          {/* End main area */}
        </>
      ) : isLoading ? (
        <div className={classNames(` flex justify-center items-center w-full`)}>
          <LoadingSpinner size={32} />
        </div>
      ) : (
        <div className="flex items-center  justify-center w-full h-full px-4">
          <PlaceHolder
            img={SessionPlaceholder}
            description={"studentNoSessions"}
          />
        </div>
      )}
    </div>
  );
}
export default SessionsPage;
